// @flow
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useI18n } from '@shopify/react-i18n';
import queryString from 'query-string';

import { routes } from 'src/routes';
import history from 'src/routes/history';
import { getGA4OAuthUrl, disconnectGA4, getGA4OAuthInformation, switchGA4Property } from 'src/services/apis/ga4.api';
import { trackFSEvent } from 'src/integrations/fullStory';

const GA4_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
};

type Props = {
  location: Object,
  queryParams: string,
}

const useGA4 = ({ location, queryParams }: Props) => {
  const [i18n] = useI18n();
  const [isLoading, setIsLoading] = useState(false);
  const [googleInformation, setGoogleInformation] = useState(null);
  const [statusPermission, setStatusPermission] = useState('');
  const [isConfirmDisconnect, setIsConfirmDisconnect] = useState(false);
  const [errorCode, setErrorCode] = useState('');

  const isEnabled = googleInformation && googleInformation.status === GA4_STATUS.ACTIVE;

  useEffect(() => {
    const queries = queryString.parse(location.search);
    if (!_.isEmpty(queries.status)) {
      setStatusPermission(queries.status);
    }
    if (!_.isEmpty(queries.errorCode)) {
      setErrorCode(queries.errorCode);
    }
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    setIsLoading(true);
    const { response, type } = await getGA4OAuthInformation(i18n);
    if (type === 'success' && response) {
      setGoogleInformation(response.data);
    }
    setIsLoading(false);
  };

  const handleConnect = async () => {
    setIsLoading(true);
    const redirectUri = `${
      process.env.REACT_APP_FILTER_ADMIN_HTML_BASE_URL
    }${routes.integration['google-analytics-4'].callback()}`;

    const { response, type } = await getGA4OAuthUrl(redirectUri, i18n);

    if (type === 'success' && response) {
      const authUrl = _.get(response, 'data.url', '') || _.get(response, 'data.authUrl', '');
      if (authUrl) {
        const shopifyParams = queryString.parse(queryParams || location.search);
        const queryArrays = Object.keys(shopifyParams).map(key => `${key}|${shopifyParams[key]}`);
        const queryStringify = queryArrays.join(',');

        // Append shopify params to the state parameter inside the URL
        const url = new URL(authUrl);
        const currentState = url.searchParams.get('state') || '';
        url.searchParams.set('state', `${currentState},${queryStringify}`);
        window.parent.location = url.toString();
        return;
      }
    }
    setIsLoading(false);
  };

  const handleDisconnect = async () => {
    setIsLoading(true);
    const { type } = await disconnectGA4(i18n);
    if (type === 'success') {
      setGoogleInformation(prev => ({ ...prev, status: GA4_STATUS.INACTIVE }));
    }
    trackFSEvent('Turn off Integration', {
      feature: 'Integration',
      appName: `Google Analytics 4`
    });
    setIsConfirmDisconnect(false);
    setIsLoading(false);
  };

  const handleCheckShowConfirm = () => {
    if (isEnabled) {
      setIsConfirmDisconnect(true);
    } else {
      handleConnect();
    }
  };

  const handleCloseConfirm = () => {
    setIsConfirmDisconnect(false);
  };

  const onDismissStatus = () => {
    setStatusPermission('');
    history.replace(routes.integration['google-analytics-4'].view());
  };

  const handleSwitchProperty = async (propertyId) => {
    setIsLoading(true);
    const { response, type } = await switchGA4Property(propertyId, i18n);
    if (type === 'success' && response) {
      setGoogleInformation(prev => ({
        ...prev,
        ...response.data,
      }));
    }
    setIsLoading(false);
  }

  return {
    i18n,
    isLoading,
    isEnabled,
    googleInformation,
    statusPermission,
    isConfirmDisconnect,
    errorCode,
    handleConnect,
    handleDisconnect,
    handleCheckShowConfirm,
    handleCloseConfirm,
    onDismissStatus,
    handleSwitchProperty,
  };
};

export default useGA4;
